import { replaceEntities, updateEntities } from '../entities/slice';
import type { AppMiddleware } from '../types';

export const NEED_TO_GET_AUTH_URL_MARKER = 'NEED_TO_GET_AUTH';

type UrlEntity = { url?: string | null };

const isUsableUrl = (url: string | null | undefined): url is string =>
  typeof url === 'string' &&
  url.length > 0 &&
  !url.includes(NEED_TO_GET_AUTH_URL_MARKER);

const preserveSignedUrls = <Entity extends UrlEntity>(
  payloadEntities: Record<string, Partial<Entity>>,
  stateEntities: Record<string, Entity>,
): Record<string, Partial<Entity>> =>
  Object.fromEntries(
    Object.entries(payloadEntities).map(
      ([id, payloadEntity]): [string, Partial<Entity>] => {
        const existingUrl = stateEntities[id]?.url;
        if (isUsableUrl(existingUrl) && !isUsableUrl(payloadEntity.url)) {
          return [id, { ...payloadEntity, url: existingUrl }];
        }
        return [id, payloadEntity];
      },
    ),
  );

export const entityIgnoreNeedAuthUrlUpdateMiddleware: AppMiddleware =
  (api) => (next) => (action) => {
    if (!updateEntities.match(action) && !replaceEntities.match(action)) {
      return next(action);
    }

    const { files, proxies, keyframes } = action.payload;
    if (!files && !proxies && !keyframes) {
      return next(action);
    }

    const {
      files: stateFiles,
      proxies: stateProxies,
      keyframes: stateKeyframes,
    } = api.getState().entities;
    return next({
      ...action,
      payload: {
        ...action.payload,
        ...(files ? { files: preserveSignedUrls(files, stateFiles) } : {}),
        ...(proxies
          ? { proxies: preserveSignedUrls(proxies, stateProxies) }
          : {}),
        ...(keyframes
          ? { keyframes: preserveSignedUrls(keyframes, stateKeyframes) }
          : {}),
      },
    });
  };
